import { useEffect, useState } from 'react';
import {
  LinkOutlined,
  LogoutOutlined,
  ReloadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import { backendUrl } from '../../api/http';
import { listActivities } from '../../api/activities';
import { buildProfileSummary } from '../../api/profile';
import './Profile.css';

function formatDate(value) {
  if (!value) return 'Chưa có';

  return new Intl.DateTimeFormat('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(value));
}

function StatTile({ label, value, unit }) {
  return (
    <div className="profile-stat-tile">
      <strong>
        {value}
        {unit && <span>{unit}</span>}
      </strong>
      <small>{label}</small>
    </div>
  );
}

function ProfileAvatar({ athlete }) {
  if (athlete.avatar) {
    return <img className="profile-avatar" alt={athlete.name} src={athlete.avatar} />;
  }

  return <div className="profile-avatar profile-avatar-fallback">{athlete.initials}</div>;
}

function Profile() {
  const { user, isLoggedIn, logout } = useAuth();
  const [activities, setActivities] = useState({ data: [], summary: { distance: 0 } });
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let ignore = false;

    async function loadProfile() {
      try {
        setStatus('loading');
        const result = await listActivities(user);
        if (!ignore) {
          setActivities(result);
          setStatus('ready');
        }
      } catch (err) {
        if (!ignore) setStatus('error');
      }
    }

    loadProfile();
    return () => {
      ignore = true;
    };
  }, [user]);

  const profile = buildProfileSummary(user, activities);
  const { athlete, stats, strava } = profile;
  const connectUrl = `${backendUrl}/api/connect/strava`;

  if (status === 'loading') {
    return <div className="htv-state">Đang tải hồ sơ...</div>;
  }

  if (status === 'error') {
    return <div className="htv-state">Không tải được hồ sơ. Vui lòng thử lại sau.</div>;
  }

  return (
    <section className="profile-page">
      <div className="profile-hero htv-card">
        <div className="profile-identity">
          <ProfileAvatar athlete={athlete} />
          <div>
            <p className="profile-kicker">Hồ sơ của tôi</p>
            <h1>{athlete.name}</h1>
            <p className="profile-meta">
              <UserOutlined /> {athlete.location}
            </p>
            {athlete.email && <p className="profile-meta">{athlete.email}</p>}
          </div>
        </div>

        <div className="profile-stat-grid">
          <StatTile label="Tổng quãng đường" value={stats.totalKm} unit=" km" />
          <StatTile label="Ngày tham gia" value={stats.daysJoined} />
          <StatTile label="Ngày có hoạt động" value={stats.activeDays} />
          <StatTile label="Hoạt động" value={stats.activityCount} />
        </div>
      </div>

      <div className="profile-content-grid">
        <section className="profile-panel htv-card">
          <div className="profile-panel-heading">
            <div>
              <p className="profile-kicker">Đồng bộ</p>
              <h2>Strava</h2>
            </div>
            <span className={strava.connected ? 'profile-status is-connected' : 'profile-status'}>
              {strava.connected ? 'Đã kết nối' : 'Chưa kết nối'}
            </span>
          </div>

          <div className="profile-connection">
            <div className="profile-strava-mark">S</div>
            <div>
              <strong>Strava</strong>
              <p>
                {strava.connected
                  ? `Athlete ID: ${strava.id}`
                  : 'Kết nối Strava để đồng bộ hoạt động chạy và đi bộ.'}
              </p>
            </div>
          </div>

          <a className="profile-primary-action" href={connectUrl}>
            {strava.connected ? <ReloadOutlined /> : <LinkOutlined />}
            {strava.connected ? 'Kết nối lại Strava' : 'Kết nối Strava'}
          </a>
        </section>

        <section className="profile-panel htv-card">
          <div className="profile-panel-heading">
            <div>
              <p className="profile-kicker">Thống kê</p>
              <h2>Tổng quan cá nhân</h2>
            </div>
          </div>

          <dl className="profile-detail-list">
            <div>
              <dt>Ngày tham gia</dt>
              <dd>{formatDate(athlete.joinedAt)}</dd>
            </div>
            <div>
              <dt>Hoạt động gần nhất</dt>
              <dd>{formatDate(stats.lastActivityDate)}</dd>
            </div>
            <div>
              <dt>Nguồn dữ liệu</dt>
              <dd>{strava.connected ? 'Strava' : 'Chưa cấu hình'}</dd>
            </div>
          </dl>
        </section>
      </div>

      <button className="profile-logout" type="button" onClick={logout}>
        <LogoutOutlined />
        Đăng xuất
      </button>
    </section>
  );
}

export default Profile;
